import React, { useState, useEffect } from 'react';
import { Box, TableBody, InputAdornment, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import TotalPlansIcon from '../../../../assets/subscription/total-plans-icon.svg';
import ActivePlansIcon from '../../../../assets/subscription/active-plans.svg';
import ActiveSubscriptionsIcon from '../../../../assets/subscription/active-subscription.svg';
import { 
  fetchSubscriptionPlans, 
  fetchSubscriptionCounts, 
  selectSubscriptionPlans, 
  selectSubscriptionPlansLoading, 
  selectSubscriptionCounts,
  selectSubscriptionPlansCount
} from '../../../../modules/subscription';
import CustomTableHeader from '../../../../components/CustomHeader';
import CustomPagination from '../../../../components/CustomPagination';
import Loader from '../../../../components/common/Loader';

import {
  PageWrapper,
  SummaryCardsContainer,
  SummaryCard,
  IconWrapper,
  CardInfo,
  CardTitle,
  CardValue,
  MainContentContainer,
  ContentHeader,
  SectionTitle,
  ActionsGroup,
  SearchField,
  CreatePlanButton,
  StyledTabs,
  StyledTab,
  TableWrapper,
  SubscriptionTable,
  SubscriptionTableRow,
  SubscriptionTableCell,
  StatusBadge
} from './styles';

const SubscriptionsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  
  const plans = useSelector(selectSubscriptionPlans) || [];
  const loading = useSelector(selectSubscriptionPlansLoading);
  const counts = useSelector(selectSubscriptionCounts) || { total_plans: 0, active_plans: 0, active_subscriptions: 0 };
  const totalPlans = useSelector(selectSubscriptionPlansCount);

  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('created_on');
  const rowsPerPage = 10;

  useEffect(() => {
    setActivePage('subscriptions');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Subscriptions', path: '/super-admin/subscriptions' },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow]);

  useEffect(() => {
    dispatch(fetchSubscriptionCounts());
  }, [dispatch]);

  useEffect(() => {
    const status = tabValue === 0 ? 'All' : tabValue === 1 ? 'Active' : 'Inactive';
    dispatch(fetchSubscriptionPlans({ 
      page, 
      pageSize: rowsPerPage, 
      status, 
      search: searchTerm 
    }));
  }, [dispatch, page, tabValue, searchTerm]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const SUBSCRIPTION_COLUMNS = [
    { id: 'plan_name', label: 'Plan Name' },
    { id: 'plan_price_month', label: 'Price' },
    { id: 'plan_expiry_in_days', label: 'Validity' },
    { id: 'number_of_staff_users', label: 'Users Allowed' },
    { id: 'features', label: 'Plan Features', disableSorting: true },
    { id: 'orgs_using', label: 'Orgs using', disableSorting: true },
    { id: 'is_active', label: 'Status' },
  ];

  return (
    <PageWrapper>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#333' }}>
        Manage Subscriptions
      </Typography>

      <SummaryCardsContainer>
        <SummaryCard>
          <IconWrapper bgcolor="#FFF8E1">
            <img src={TotalPlansIcon} alt="Total Plans" style={{ width: '24px', height: '24px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>Total Plans</CardTitle>
            <CardValue>{counts.total_plans}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <IconWrapper bgcolor="#E8F5E9">
            <img src={ActivePlansIcon} alt="Active Plans" style={{ width: '24px', height: '24px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>Active Plans</CardTitle>
            <CardValue>{counts.active_plans}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <IconWrapper bgcolor="#E3F2FD">
            <img src={ActiveSubscriptionsIcon} alt="Active Subscriptions" style={{ width: '24px', height: '24px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>Active Subscriptions</CardTitle>
            <CardValue>{counts.active_subscriptions}</CardValue>
          </CardInfo>
        </SummaryCard>
      </SummaryCardsContainer>

      <MainContentContainer>
        {loading && <Loader open={loading} />}
        <ContentHeader>
          <SectionTitle>All Subscription Plans</SectionTitle>
          <ActionsGroup>
            <SearchField
              placeholder="Search Plans"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999', fontSize: '18px' }} />
                  </InputAdornment>
                ),
              }}
            />
            <CreatePlanButton 
              variant="contained" 
              size="small"
              startIcon={<AddIcon />} 
              disableElevation
              onClick={() => navigate('/super-admin/subscriptions/create')}
            >
              Create Plan
            </CreatePlanButton>
          </ActionsGroup>
        </ContentHeader>

        <StyledTabs value={tabValue} onChange={handleTabChange}>
          <StyledTab label="All" />
          <StyledTab label="Active" />
          <StyledTab label="Inactive" />
        </StyledTabs>

        <TableWrapper>
          <SubscriptionTable>
            <CustomTableHeader
              columns={SUBSCRIPTION_COLUMNS}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {plans.map((plan: any) => (
                <SubscriptionTableRow 
                  key={plan.plan_id}
                  onClick={() => navigate(`/super-admin/subscriptions/${plan.plan_id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <SubscriptionTableCell sx={{ fontWeight: 500 }}>{plan.plan_name}</SubscriptionTableCell>
                  <SubscriptionTableCell>${plan.plan_price_month}</SubscriptionTableCell>
                  <SubscriptionTableCell>{plan.plan_expiry_in_days} Days</SubscriptionTableCell>
                  <SubscriptionTableCell>{plan.number_of_staff_users}</SubscriptionTableCell>
                  <SubscriptionTableCell>
                    {`${Array.isArray(plan.plan_modules) ? plan.plan_modules.length : 0} ${
                      (Array.isArray(plan.plan_modules) ? plan.plan_modules.length : 0) === 1 ? 'feature' : 'features'
                    }`}
                  </SubscriptionTableCell>
                  <SubscriptionTableCell>
                    {`${plan.orgs_using || 0} ${
                      (plan.orgs_using || 0) === 1 || (plan.orgs_using || 0) === 0 ? 'org' : 'orgs'
                    }`}
                  </SubscriptionTableCell>
                  <SubscriptionTableCell>
                    <StatusBadge status={plan.is_active ? 'Active' : 'Inactive'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </SubscriptionTableCell>

                </SubscriptionTableRow>
              ))}
              {plans.length === 0 && !loading && (
                <SubscriptionTableRow>
                  <SubscriptionTableCell colSpan={7} align="center">
                    No subscription plans found.
                  </SubscriptionTableCell>
                </SubscriptionTableRow>
              )}
            </TableBody>
          </SubscriptionTable>
        </TableWrapper>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {plans.length} of {totalPlans} results
          </Typography>
          <CustomPagination
            totalPages={Math.ceil(totalPlans / rowsPerPage)}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContentContainer>
    </PageWrapper>
  );
};

export default SubscriptionsPage;
